import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaFacebookF, FaInstagram, FaTiktok } from 'react-icons/fa'

const Footer = () => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const companyLinks = [
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.contact'), href: '/contact' },
    { name: 'AGB', href: '/terms' },
    { name: 'Impressum', href: '/impressum' },
    { name: 'Datenschutz', href: '/privacy' },
  ]

  const serviceLinks = [
    { name: 'Wunschkennzeichen', href: '/wunschkennzeichen' },
    { name: t('shop.tabs.accessories'), href: '/shop#accessories' },
  ]

  const socialLinks = [
    { icon: FaFacebookF, href: 'https://facebook.com/kennzeichenpro24', label: 'Facebook' },
    { icon: FaInstagram, href: 'https://instagram.com/kennzeichenpro24', label: 'Instagram' },
    { icon: FaTiktok, href: 'https://tiktok.com/@kennzeichenpro24', label: 'TikTok' },
    { icon: FaWhatsapp, href: 'https://wa.me/4915565553294', label: 'WhatsApp' },
  ]

  return (
    <footer className="relative bg-dark-900/80 backdrop-blur-xl border-t border-dark-700/50">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

      <div className="container-custom px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">

          {/* Company Info + Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="sm:col-span-2 lg:col-span-1"
          >
            <h3 className="text-white text-xl sm:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">
              KennzeichenPro24
            </h3>
            <p className="mb-5 text-gray-400 leading-relaxed text-sm">
              {t('home.subtitle')}
            </p>
            <div className="space-y-3">
              <a href="tel:+4955149203480" className="flex items-center gap-3 text-gray-400 hover:text-primary-400 transition-colors group">
                <div className="p-1.5 rounded-lg bg-dark-800 group-hover:bg-primary-500/10 transition-colors">
                  <FaPhone className="w-3.5 h-3.5" />
                </div>
                <span className="text-sm">+49 (0) 551 49203480</span>
              </a>
              <a href="https://wa.me/4915565553294" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-green-400 transition-colors group">
                <div className="p-1.5 rounded-lg bg-dark-800 group-hover:bg-green-500/10 transition-colors">
                  <FaWhatsapp className="w-3.5 h-3.5" />
                </div>
                <span className="text-sm">+49 155 65553294</span>
              </a>
              <a href="mailto:info@kennzeichenpro24.de" className="flex items-center gap-3 text-gray-400 hover:text-primary-400 transition-colors group">
                <div className="p-1.5 rounded-lg bg-dark-800 group-hover:bg-primary-500/10 transition-colors">
                  <FaEnvelope className="w-3.5 h-3.5" />
                </div>
                <span className="text-sm">info@kennzeichenpro24.de</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <div className="p-1.5 rounded-lg bg-dark-800 mt-0.5">
                  <FaMapMarkerAlt className="w-3.5 h-3.5" />
                </div>
                <span className="text-sm">Zollstock 15, 37081<br />Göttingen-Grone, Niedersachsen</span>
              </div>
            </div>
          </motion.div>

          {/* Services Links + Opening Hours */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-white text-lg sm:text-xl font-semibold mb-4 sm:mb-6">{t('footer.shop')}</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>

            {/* Opening Hours */}
            <div className="mt-6">
              <h4 className="text-white text-lg font-semibold mb-3 flex items-center gap-2">
                <FaClock className="w-4 h-4 text-primary-400" />
                {t('footer.openingHours')}
              </h4>
              <div className="space-y-1.5 text-sm text-gray-400">
                <div className="flex justify-between gap-4">
                  <span>Mo – Fr</span>
                  <span className="text-gray-300">09:00 – 15:30</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Sa</span>
                  <span className="text-gray-300">10:00 – 14:00</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>So</span>
                  <span className="text-red-400/80">Geschlossen</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-white text-lg sm:text-xl font-semibold mb-4 sm:mb-6">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {companyLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Customer Service + Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-white text-lg sm:text-xl font-semibold mb-4 sm:mb-6">
              {t('footer.customerService')}
            </h4>
            <ul className="space-y-3">
              {[
                { name: t('nav.account'), href: '/account' },
                { name: t('nav.orders'), href: '/orders' },
                { name: t('nav.contact'), href: '/contact' },
              ].map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="mt-6">
              <h4 className="text-white text-lg font-semibold mb-3">{t('footer.followUs')}</h4>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.15, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-dark-800 text-gray-400 hover:text-white hover:bg-primary-500/20 border border-dark-700/50 hover:border-primary-500/30 transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon className="w-4 h-4" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-dark-700/50 mt-8 sm:mt-12 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {currentYear} KennzeichenPro24 GmbH. {t('footer.rights')}.
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <Link to="/impressum" className="hover:text-gray-400 transition-colors">Impressum</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-gray-400 transition-colors">Datenschutz</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-gray-400 transition-colors">AGB</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
